"use client";

import { useState, useEffect } from "react";
import { X, ArrowRight } from "lucide-react";
import Link from "next/link";
import { useCookieConsent } from "@lightwaves/hooks";

interface AnnouncementBannerProps {
  id: string;
  message: string;
  ctaText?: string;
  ctaHref?: string;
}

export function AnnouncementBanner({ id, message, ctaText, ctaHref }: AnnouncementBannerProps) {
  const { hasConsent } = useCookieConsent();
  const [isDismissed, setIsDismissed] = useState(true);
  const storageKey = `announcement-dismissed-${id}`;

  useEffect(() => {
    const dismissed = localStorage.getItem(storageKey);
    setIsDismissed(dismissed === "true");
  }, [storageKey]);

  const handleDismiss = () => {
    localStorage.setItem(storageKey, "true");
    setIsDismissed(true);
    // Dispatch custom event so navbar can update immediately
    window.dispatchEvent(new CustomEvent('announcement-dismissed', { detail: { id } }));
  };

  if (!hasConsent || isDismissed) return null;

  return (
    <div className="fixed top-0 left-0 right-0 w-full h-9 bg-primary text-primary-foreground px-4 flex items-center justify-center z-[60] animate-in fade-in slide-in-from-top duration-500">
      <div className="container mx-auto flex items-center justify-center gap-3 text-[10px] md:text-[11px] font-bold tracking-[0.15em] uppercase">
        <span>{message}</span>
        {ctaText && ctaHref && (
          <Link 
            href={ctaHref} 
            className="flex items-center gap-1 underline underline-offset-4 hover:opacity-80 transition-opacity whitespace-nowrap"
          >
            {ctaText} <ArrowRight size={12} />
          </Link>
        )}
      </div>
      <button 
        onClick={handleDismiss} 
        className="absolute right-2 md:right-4 top-1/2 -translate-y-1/2 p-1 opacity-60 hover:opacity-100 transition-opacity"
        aria-label="Dismiss"
      >
        <X size={14} />
      </button>
    </div>
  );
}